import { memo, useEffect, useState } from 'react';
import { autoLogin, login, useAuth } from './redux/ducks/auth';
import { isBooleanTrue, isStringNotEmpty } from './utils/objectUtils';
import { getEmailInfo, getPhoneInfo, useAnalytics, getDeviceFingerprint, getIPAddress, getReferrerUrlAction, getUserAgentAction, updateAnalyticsAndAffiliateInfo } from './redux/ducks/analytics';
// import { useCollectJS } from './redux/ducks/collectjs';
import { useKlarna } from './redux/ducks/klarna';
import { useDispatch, useStore } from 'react-redux';
import { auth } from './firebase';
import { calculateCartSummary, recheckCouponForCart, updateCouponCode, useCart } from './redux/ducks/cart';
import { useDeepCompareEffectNoCheck } from './hooks/useDeepCompareEffect';
import { useSinglePaymentPlanOnly } from './hooks/useSinglePaymentPlanOnly';
import { jwtDecode } from 'jwt-decode';
import { getAgreements, getFunnelBootstrap, getOrchestrator, initOrchestratorVersionListeners } from './redux/ducks/orchestrator';
import { isBundleScopedRoute, isFunnelFastBootEnabled, normalizeFunnelRoute, runAfterFirstPaint } from './utils/funnelRouteUtils';
import { shouldHideIntercomLauncher } from './utils/intercomUtils';
import TrackingService from './services/TrackingService';
import logger from './utils/logger';
import trackingConsole from './utils/trackingConsole';
import { getErrorMessage } from './utils/errorUtils';
import Intercom from '@intercom/messenger-js-sdk';
import SupportChatWidget from './components/SupportChat/SupportChatWidget';

// the static boot splash in public/index.html paints before the bundle loads; fade it out once
// the app is ready to render real content
const hideBootSplash = () => {
	const splash = document.getElementById('rrx-splash');
	if (splash) {
		splash.classList.add('rrx-splash--hide');
		setTimeout(() => { splash.remove(); }, 300);
	}
};

const AppWrapper = ({ children }) => {
	//#region useDispatch

	const dispatch = useDispatch();

	//#endregion
	//#region useStore

	const store = useStore();

	//#endregion
	//#region useStates

	const [ready, setReady] = useState(false);

	//#endregion
	//#region useAuth

	const { profile } = useAuth();

	//#endregion
	//#region useAnalytics

	const { couponCode } = useAnalytics();

	//#endregion
	//#region useCart

	const { cartItems, shippingMethod, coupon, taxRate } = useCart();

	//#endregion
	// //#region useCollectJS

	// const { setCollectJS, setPaymentTokenResponse, setIsCCFieldsAvailable, addError, unsetErrors } = useCollectJS();

	// //#endregion
	//#region useKlarna

	const { setKlarnaInstance, setAuthorizationTokenResponse, setClientTokenResponse } = useKlarna();

	//#endregion
	//#region useEffects

	useEffect(() => {
		let unsubscribe;
		// if this effect is cleaned up before the deferred callbacks run, they must not attach
		// version listeners that nothing would ever detach
		let disposed = false;
		trackingConsole.log('Bootstrapping orchestrator...');

		// local-debug telemetry only (DevTools User Timing) - nothing reports these; the
		// measure spans make each boot phase readable without a reporting pipeline
		const mark = (name, measureFrom = null) => {
			try {
				if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
					performance.mark(name);
					if (measureFrom !== null && typeof performance.measure === 'function') {
						performance.measure(`${measureFrom} -> ${name}`, measureFrom, name);
					}
				}
			} catch (e) { /* perf marks are best-effort */ }
		};

		const initIntercom = () => {
			try {
				Intercom({
					app_id: process.env.REACT_APP_INTERCOM_APP_ID,
					hide_default_launcher: shouldHideIntercomLauncher(window.location.pathname),
				});
				// console.log('Intercom initialized');
			} catch (err) {
				logger.logErrorEvent('INTERCOM_INITIALIZATION', err, getErrorMessage(err), true);
			}
		};

		const initializeTracking = async () => {
			try {
				if (dispatch) {
					trackingConsole.log('Initializing Tracking Service');
					// Set dispatch for analytics updates
					TrackingService.setDispatch(dispatch);

					// Initialize tracking
					const trackingData = await TrackingService.initialize();
					trackingConsole.log('trackingData after initialize', trackingData);

					if (trackingData?.siteVisit) {
						dispatch(updateAnalyticsAndAffiliateInfo({
							siteVisit: trackingData.siteVisit,
							siteVisitId: trackingData.siteVisit.id,
							affiliateInfo: trackingData.affiliateInfo,
							deviceFingerprint: trackingData.deviceFingerprint ?? null,
							...trackingData.trackingData
						}));
					}

				}
			} catch (err) {
				logger.logErrorEvent('TRACKING_SERVICE_INITIALIZATION', err, getErrorMessage(err), true);
			}
		};

		(async () => {
			mark('rrx:boot:start');
			try {
				// 1) initial cache + fetch
				dispatch(autoLogin());
				dispatch(getReferrerUrlAction());
				dispatch(getUserAgentAction());
				dispatch(getIPAddress());

				// 2) start tracking immediately (network-bound, no orchestrator dependency) so the
				// siteVisit is being created while data loads instead of after it
				initializeTracking();

				const funnelRoute = normalizeFunnelRoute(window.location.pathname, window.location.search);
				const fastBootEnabled = isFunnelFastBootEnabled(window.location.search);
				const fastBoot = funnelRoute.isFunnelRoute && fastBootEnabled;
				// the encounter funnel renders from a bundle too, but its program is only known once
				// the authed encounter fetch resolves, so the page loads it - boot just paints
				const encounterFastBoot = fastBootEnabled && funnelRoute.isFunnelRoute === false && isBundleScopedRoute(window.location.pathname);

				if (funnelRoute.isFunnelRoute) {
					// download the lazy route chunk during the data load instead of after ready -
					// same module the route's lazy() resolves, so webpack reuses the chunk
					const prefetchFunnelChunk = funnelRoute.isCheckoutRoute
						? () => import('./pages/Products/Funnels/FunnelCheckout')
						: () => import('./pages/Products/Funnels');
					prefetchFunnelChunk().catch(() => { /* the route's own lazy import retries */ });
				}

				if (fastBoot) {
					// 3a) funnel fast boot: load only the funnel-critical collections, paint, then
					// hydrate the rest (and third parties) after first paint
					await dispatch(getFunnelBootstrap(false, funnelRoute.isCheckoutRoute, funnelRoute.programSlug));
					mark('rrx:boot:funnelBootstrapLoaded', 'rrx:boot:start');

					setReady(true);
					mark('rrx:boot:ready', 'rrx:boot:start');

					runAfterFirstPaint(async () => {
						try {
							// agreements are not in the bundle and the CREATE_ACCOUNT step renders
							// their links, so a fast clicker must not outrun them
							await dispatch(getAgreements(false));
							initIntercom();
						} catch (e) {
							trackingConsole.error('Error loading agreements after funnel fast boot', e);
						}
					});

					// the full catalog is NOT hydrated here: replacing the bundle's collections mid-funnel
					// re-renders the funnel and stalls input on every step, checkout included. Scope stays
					// FUNNEL_BUNDLE and OrchestratorHydrator loads the rest once the customer leaves.
				} else if (encounterFastBoot) {
					// paint immediately; the encounter page boots its own program bundle once it knows
					// which program the encounter belongs to
					setReady(true);
					mark('rrx:boot:ready', 'rrx:boot:start');

					runAfterFirstPaint(() => { initIntercom(); });
				} else {
					// 3b) Wait for orchestrator to finish loading before attaching listeners.
					// This prevents listeners from seeing null versions and re-fetching everything.
					await dispatch(getOrchestrator());
					mark('rrx:boot:orchestratorLoaded', 'rrx:boot:start');
					if (disposed) { return; }

					// 4) Attach Firestore listeners for all tables in the orchestrator
					unsubscribe = initOrchestratorVersionListeners(store);

					// 5) initiate 3rd party services
					initIntercom();

					// 6) show the app
					setReady(true);
					mark('rrx:boot:ready', 'rrx:boot:start');
				}
			} catch (e) {
				trackingConsole.error("Error bootstrapping orchestrator", e);
				// You might still want to show the app with degraded state:
				setReady(true);
			}
		})();

		trackingConsole.log('Bootstrapping orchestrator DONE...');

		return () => {
			disposed = true;
			if (typeof unsubscribe === "function") {
				unsubscribe();
			}
		};
	}, [dispatch, store]);

	useEffect(() => {
		if (ready) {
			hideBootSplash();
		}
	}, [ready]);

	useEffect(() => {
		if (isStringNotEmpty(profile?.phone)) {
			dispatch(getPhoneInfo(profile?.phone));
		}
	}, [profile?.phone]);

	useEffect(() => {
		if (isStringNotEmpty(profile?.email)) {
			dispatch(getEmailInfo(profile?.email));
		}
	}, [profile?.email]);

	useEffect(() => {
		// console.log('couponCode', couponCode);
		// console.log('profile?.email', profile?.email);
		// console.log('userId', profile?.userId);
		// a friends and family profile still needs the dispatch when tracking never resolved a coupon
		// code - updateCouponCode resolves the account's own code from an empty one
		if (isStringNotEmpty(couponCode) || isBooleanTrue(profile?.isFriendsAndFamily)) {
			dispatch(updateCouponCode(couponCode, profile?.userId, profile?.email));
		}
	}, [couponCode, profile?.email, profile?.userId, profile?.isFriendsAndFamily]);

	// the attached coupon follows the cart, re-checked in memory on every product, dose or plan change
	useDeepCompareEffectNoCheck(() => {
		dispatch(recheckCouponForCart());
	}, [cartItems]);

	useSinglePaymentPlanOnly({ cartItems, coupon, isFriendsAndFamily: profile?.isFriendsAndFamily });

	useDeepCompareEffectNoCheck(() => {
		// console.log('recalculating cart summary....');
		dispatch(calculateCartSummary());
	}, [cartItems, shippingMethod, coupon, taxRate]);

	// useEffect(() => {
	// 	const script = document.createElement('script');

	// 	script.src = 'https://secure.nmi.com/token/Collect.js';
	// 	script.async = true;
	// 	script.defer = true;
	// 	script.type = 'text/javascript';
	// 	script.setAttribute('data-tokenization-key', process.env.REACT_APP_NMI_TOKENIZATION_KEY);
	// 	script.setAttribute('data-variant', 'inline');


	// 	document.querySelector('body').appendChild(script);

	// 	new Promise((resolve) => {
	// 		script.onload = function () {
	// 			resolve(window.CollectJS);
	// 		}
	// 	}).then((collectJSConnection) => {
	// 		// console.log('injected collectjs');
	// 		collectJSConnection.configure({
	// 			//"paymentSelector": "#payButton",
	// 			"variant": "inline",
	// 			"styleSniffer": "true",
	// 			"googleFont": "Abel",
	// 			invalidCss: {
	// 				color: 'black',
	// 				'border-color': '#dc3545',
	// 				'border-style': 'solid',
	// 				'border-width': '1px'
	// 			},
	// 			validCss: {
	// 				color: 'black',
	// 				'border-color': '#198754',
	// 				'border-style': 'solid',
	// 				'border-width': '1px'
	// 			},
	// 			customCss: {
	// 				'border-color': '#cbd5e1',
	// 				'border-style': 'solid',
	// 				'border-width': '1px'
	// 			},
	// 			focusCss: {
	// 				color: 'black',
	// 				'border-color': '#8090a5',
	// 				'border-style': 'solid',
	// 				'border-width': '1px'
	// 			},
	// 			"fields": {
	// 				"ccnumber": {
	// 					"display": "required",
	// 					"selector": "#ccnumber",
	// 					"enableCardBrandPreviews": "true"
	// 				},
	// 				"ccexp": {
	// 					"display": "required",
	// 					"selector": "#ccexp",
	// 					"placeholder": "MM / YY *"
	// 				},
	// 				"cvv": {
	// 					"display": "required",
	// 					"selector": "#cvv"
	// 				},
	// 			},
	// 			callback: function (e) {
	// 				// console.log('CollectJS Callback', e);
	// 				setPaymentTokenResponse(e);
	// 			},
	// 			validationCallback: function (fieldName, valid, message) {
	// 				// console.log('CollectJS Validation Callback', fieldName, valid, message);
	// 				if (valid) {
	// 					unsetErrors(fieldName);
	// 				} else {
	// 					unsetErrors(fieldName);
	// 					addError(fieldName, message);
	// 				}
	// 			},
	// 			fieldsAvailableCallback: function () {
	// 				// console.log("Collect.js loaded the fields onto the form.");
	// 				setIsCCFieldsAvailable(true);
	// 			}
	// 		});
	// 		setCollectJS(collectJSConnection);
	// 	});
	// 	// No dependencies - we don't ever want this to run more than once. Calling this more times will cause fields to blink.

	// 	return () => {
	// 		// Cleanup: Remove the script when the component unmounts
	// 		if (document.body.contains(script)) {
	// 			document.body.removeChild(script);
	// 		}

	// 		if (setCollectJS) {
	// 			setCollectJS(null);
	// 		}
	// 		window.CollectJS = null;
	// 		console.log('cleaned up collectjs');
	// 	};
	// }, []);

	useEffect(() => {
		// Klarna's SDK is only needed at checkout; inject it after first paint so the script
		// download never competes with boot data loading.
		if (!ready) { return undefined; }

		let script = null;
		let removed = false;

		runAfterFirstPaint(() => {
			if (removed) { return; }

			script = document.createElement('script');

			script.src = 'https://x.klarnacdn.net/kp/lib/v1/api.js';
			script.async = true;
			script.defer = true;
			script.type = 'text/javascript';

			script.setAttribute('data-tokenization-key', process.env.REACT_APP_NMI_TOKENIZATION_KEY);

			document.querySelector('body').appendChild(script);

			new Promise((resolve) => {
				script.onload = function () {
					resolve(window.Klarna);
				}
			}).then((klarnaConnection) => {
				// console.log('injected Klarna');

				setKlarnaInstance(klarnaConnection);
			});
		});
		// Only runs once, when ready flips true. Running more times will cause fields to blink.

		return () => {
			removed = true;

			// Cleanup: Remove the script when the component unmounts
			if (script && document.body.contains(script)) {
				document.body.removeChild(script);
			}

			if (setKlarnaInstance) {
				setKlarnaInstance(null);
			}
			window.Klarna = null;

			setClientTokenResponse(null);
			setAuthorizationTokenResponse(null);
			trackingConsole.log('cleaned up Klarna');
		};
	}, [ready]);

	useEffect(() => {
		let previousUser = null;

		const unsubscribe = auth.onIdTokenChanged((authUser) => {
			trackingConsole.log('onIdTokenChanged');

			// User is signed in or token was refreshed.
			const shouldRefreshUI = shouldUpdateUI(previousUser, authUser);

			if (shouldRefreshUI) {
				trackingConsole.log('shouldRefreshUI', shouldRefreshUI);
				// Refresh your UI
				// console.log(authUser);
				dispatch(login(authUser, true));
			}

			previousUser = authUser ? { ...authUser } : null;

		});

		return () => {
			unsubscribe();
			trackingConsole.log('cleaned up onIdTokenChanged');
		};
	}, []);

	const shouldUpdateUI = (prevUser, currentUser) => {
		// User signed in/out
		if (!prevUser && currentUser) return true;
		if (prevUser && !currentUser) return true;
		if (!currentUser) return true; // signed out and couldnt find previous user

		// User changed
		if (prevUser?.uid !== currentUser?.uid) return true;

		// Check if meaningful properties changed
		if (prevUser?.email !== currentUser?.email) return true;
		if (prevUser?.emailVerified !== currentUser?.emailVerified) return true;
		if (prevUser?.displayName !== currentUser?.displayName) return true;

		let prevUserAccessTokenDecoded = jwtDecode(prevUser?.accessToken);
		let currentUserAccessTokenDecoded = jwtDecode(currentUser?.accessToken);
		let prevUserHasMFA = (prevUserAccessTokenDecoded?.firebase?.sign_in_second_factor === 'totp' || prevUserAccessTokenDecoded?.firebase?.sign_in_second_factor === 'phone') && isStringNotEmpty(prevUserAccessTokenDecoded?.firebase?.second_factor_identifier);
		let currentUserHasMFA = (currentUserAccessTokenDecoded?.firebase?.sign_in_second_factor === 'totp' || currentUserAccessTokenDecoded?.firebase?.sign_in_second_factor === 'phone') && isStringNotEmpty(currentUserAccessTokenDecoded?.firebase?.second_factor_identifier);
		if (prevUserHasMFA !== currentUserHasMFA) return true;
		if (prevUserAccessTokenDecoded?.isRep !== currentUserAccessTokenDecoded?.isRep) return true;
		if (prevUserAccessTokenDecoded?.isAdmin !== currentUserAccessTokenDecoded?.isAdmin) return true;
		if (prevUserAccessTokenDecoded?.isSuperAdmin !== currentUserAccessTokenDecoded?.isSuperAdmin) return true;
		if (prevUserAccessTokenDecoded?.isAffiliateUser !== currentUserAccessTokenDecoded?.isAffiliateUser) return true;
		if (prevUserAccessTokenDecoded?.isPartnerUser !== currentUserAccessTokenDecoded?.isPartnerUser) return true;
		if (prevUserAccessTokenDecoded?.isCoveredEntityUser !== currentUserAccessTokenDecoded?.isCoveredEntityUser) return true;
		if (prevUserAccessTokenDecoded?.isHelpStaff !== currentUserAccessTokenDecoded?.isHelpStaff) return true;
		if (prevUserAccessTokenDecoded?.isOwner !== currentUserAccessTokenDecoded?.isOwner) return true;
		if (prevUserAccessTokenDecoded?.isStaffManager !== currentUserAccessTokenDecoded?.isStaffManager) return true;
		if (prevUserAccessTokenDecoded?.isMarketingManager !== currentUserAccessTokenDecoded?.isMarketingManager) return true;
		if (prevUserAccessTokenDecoded?.phoneVerified !== currentUserAccessTokenDecoded?.phoneVerified) return true;

		// Token refresh only - no UI update needed
		return false;
	};

	// FCM/notification setup lives in src/components/NotificationSetup (inside the Router) so it
	// can re-evaluate on SPA route changes - the funnel never shows the permission prompt

	//#endregion

	if (!ready) {
		// the static boot splash in index.html covers the entire pre-ready window on a full page
		// load (it is only removed when ready flips), so rendering a loader here just stacks a
		// darkened overlay on top of it; SPA navigations never see this branch
		return null;
	}

	return (
		<>
			{children}
			{/* <SupportChatWidget /> */}
		</>
	);
};

export default memo(AppWrapper);